/**
 * Search coordination: owns the worker's lifetime and streams its hits to the
 * page. At most one search runs; typing another character supersedes the
 * previous query, and the old worker is terminated rather than left to finish
 * work nobody will look at. Ported from the desktop's `main/search.ts`.
 */
import type { SearchFlags, SearchHit } from './types'
import type { SearchJob } from './search.worker'
import { directoryFor, getOpenFolder, snapshotFolder, fileFor } from './handles'
import { parsePath } from './paths'
import { parseIgnores } from './workspace'

const MAX_FILE_BYTES = 1024 * 1024
const MAX_RESULTS = 2000

type HitListener = (payload: { id: number; hit: SearchHit }) => void
type DoneListener = (payload: { id: number; found: number }) => void
const hitListeners = new Set<HitListener>()
const doneListeners = new Set<DoneListener>()

let running: { id: number; worker: Worker } | null = null
let nextId = 1

export function onHit(fn: HitListener): () => void {
  hitListeners.add(fn)
  return () => hitListeners.delete(fn)
}

export function onDone(fn: DoneListener): () => void {
  doneListeners.add(fn)
  return () => doneListeners.delete(fn)
}

export function cancelSearch(): void {
  running?.worker.terminate()
  running = null
}

export async function startSearch(query: string, flags: SearchFlags = {}): Promise<number> {
  cancelSearch()
  const id = nextId++
  const root = getOpenFolder()
  const p = root ? parsePath(root) : null
  if (!root || !p) return -1

  let ignores: string[] = []
  try {
    ignores = [...parseIgnores(await (await fileFor(`${root}/.gitignore`, { ask: false })).text())]
  } catch {
    // No .gitignore.
  }

  const job: SearchJob = {
    root,
    query,
    caseSensitive: flags.caseSensitive === true,
    regexp: flags.regexp === true,
    wholeWord: flags.wholeWord === true,
    ignores,
    maxFileBytes: MAX_FILE_BYTES,
    maxResults: MAX_RESULTS,
  }
  if (p.scheme === 'snap') {
    const folder = snapshotFolder(p.id)
    job.files = folder ? [...folder.files].map(([path, file]) => ({ path, file })) : []
  } else {
    job.handle = await directoryFor(root, 'read')
  }

  const worker = new Worker(new URL('./search.worker.ts', import.meta.url), { type: 'module' })
  running = { id, worker }
  const finish = (found: number): void => {
    if (running?.id !== id) return
    running = null
    worker.terminate()
    for (const fn of doneListeners) fn({ id, found })
  }
  worker.onmessage = (
    e: MessageEvent<{ type: 'hit' | 'done'; hit?: SearchHit; found?: number }>
  ) => {
    if (running?.id !== id) return
    if (e.data.type === 'hit' && e.data.hit)
      for (const fn of hitListeners) fn({ id, hit: e.data.hit })
    else if (e.data.type === 'done') finish(e.data.found ?? 0)
  }
  worker.onerror = () => finish(0)
  worker.postMessage(job)
  return id
}
